/* eslint-disable no-undef */
import { api } from "@/api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { checkCustomerExists } from '../api/auth';
import { routes } from '../config';
import { cleanupAllRecaptcha, cleanupRecaptcha, getGlobalConfirmationResult, initializeRecaptcha, setGlobalConfirmationResult, setupPhoneAuth, verifyPhoneOTP } from '../lib/firebase';
import type { RootState } from '../store';
// import { useAuth } from "@/hooks";
import { useAuth } from '@/auth/AuthContext';
import { setStoredPhoneNumber, setVerificationId } from '@/store/authSlice';
import { useAppDispatch } from '@/store/hooks';
import { FirebaseUser } from "@/types";
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { useLocalization } from "@/contexts/LocalizationContext";
import { LanguageSelector } from "@/components/LanguageSelector";


export default function OTP() {
  const { t } = useLocalization();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const phoneNumber = useSelector((state: RootState) => (state as any).auth.phoneNumber);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [localConfirmationResult, setLocalConfirmationResult] = useState<any>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasNavigated = useRef(false);
  const isInitialized = useRef(false);
  const navigationAttempted = useRef(false);
  const { updateCustomer } = useAuth();
  const { storeSettings } = useStoreSettings();
  const [resendTimer, setResendTimer] = useState(60);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    let isMounted = true;

    const initRecaptcha = async () => {
      try {
        const containerId = isDesktop ? 'recaptcha-container-desktop' : 'recaptcha-container-mobile';

        // Clean up any existing reCAPTCHA first
        cleanupRecaptcha(containerId);

        // Wait a bit to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 300));

        if (isMounted) {
          await initializeRecaptcha(containerId, handleRecaptchaSolved);
          setIsRecaptchaReady(true); // reCAPTCHA v3 is ready immediately
        }
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
        if (isMounted) {
          setIsRecaptchaReady(true); // Allow proceeding even if reCAPTCHA fails
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initRecaptcha, 200);

    return () => {
      isMounted = false;
      clearTimeout(timer);
      // Clean up all reCAPTCHA instances to be safe
      cleanupAllRecaptcha();
    };
  }, [isDesktop]);

  // Handle reCAPTCHA callback
  const handleRecaptchaSolved = () => {
    setIsRecaptchaReady(true);
    setError(''); // Clear any previous errors
  };

  const handleSendOTP = async () => {
    if (!phoneNumber) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const formattedPhone = localPhoneNumber
      console.log('Sending OTP to:', formattedPhone);

      // Use different container IDs for mobile and desktop
      const containerId = isDesktop ? 'recaptcha-container-desktop' : 'recaptcha-container-mobile';
      const confirmation = await setupPhoneAuth(formattedPhone, containerId, handleRecaptchaSolved);

      console.log('OTP sent successfully');

      // Store phone number in Redux and localStorage, confirmation result in module-level storage
      dispatch(setStoredPhoneNumber(formattedPhone));
      dispatch(setVerificationId(confirmation.verificationId));
      localStorage.setItem('temp_phone_number', formattedPhone);
      setGlobalConfirmationResult(confirmation);
    } catch (error: any) {
      console.error('OTP send error:', error);

      let errorMessage = t("auth.errorMessages.otp.failed")

      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = t("auth.errorMessages.otp.invalidPhoneNumber");
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t("auth.errorMessages.otp.tooManyRequests");
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP function
  const resendOTP = async () => {
    if (resendTimer > 0) return; // Prevent multiple clicks during timer

    try {
      setIsLoading(true);
      setError('');

      setResendTimer(60);
      await handleSendOTP()
    } catch (error) {
      console.error('Error resending OTP:', error);
      setError(t("auth.errorMessages.otp.resendFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Resend timer countdown effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendTimer]);

  // Memoize the required data check
  const requiredData = useMemo(() => {
    if (isInitialized.current) {
      return { hasPhoneNumber: localPhoneNumber, hasConfirmationResult: localConfirmationResult };
    }

    // Check Redux state first, then fallback to localStorage and module-level storage
    let hasPhoneNumber = phoneNumber;
    let hasConfirmationResult = getGlobalConfirmationResult();

    // If Redux state is not available, try to get from localStorage
    if (!hasPhoneNumber) {
      const storedPhoneNumber = localStorage.getItem('temp_phone_number');
      if (storedPhoneNumber) {
        hasPhoneNumber = storedPhoneNumber;
      }
    }

    if (!hasConfirmationResult) {
      // Try to get from module-level storage
      hasConfirmationResult = getGlobalConfirmationResult();
    }

    return { hasPhoneNumber, hasConfirmationResult };
  }, [phoneNumber, localPhoneNumber, localConfirmationResult]);

  // Initialize component data - only run once
  useEffect(() => {
    if (isInitialized.current || hasNavigated.current) {
      return;
    }

    const { hasPhoneNumber, hasConfirmationResult } = requiredData;

    if (!hasPhoneNumber || !hasConfirmationResult) {
      console.log('Missing required data for OTP, redirecting to login');
      console.log('Phone number:', hasPhoneNumber);
      console.log('Confirmation result:', hasConfirmationResult);
      hasNavigated.current = true;
      navigate(routes.login);
      return;
    }

    // Set local state
    setLocalPhoneNumber(hasPhoneNumber);
    setLocalConfirmationResult(hasConfirmationResult);
    isInitialized.current = true;
  }, [requiredData, navigate]);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      hasNavigated.current = false;
      isInitialized.current = false;
      navigationAttempted.current = false;
    };
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    // Only allow single numeric digit
    if (value.length > 1) return;
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError(t("auth.errorMessages.otp.lengthInvalid"));
      return;
    }

    if (isLoading || hasNavigated.current || navigationAttempted.current) {
      return; // Prevent multiple submissions
    }

    setIsLoading(true);
    setError('');

    try {
      if (!localConfirmationResult) {
        setError(t("auth.errorMessages.otp.noSessionFound"));
        return;
      }

      const result = await verifyPhoneOTP(localConfirmationResult, otpString);

      if (result.user) {
        console.log('Firebase authentication successful:', result.user.uid);
        api.defaults.headers.Authorization = `Bearer ${result.token}`;
        try {
          const customerCheck = await checkCustomerExists();

          if (customerCheck.exists && customerCheck.customer) {
            // Customer exists - navigate to home
            const firebaseUser: FirebaseUser = {
              uid: result.user.uid,
              email: result.user.email || null,
              phoneNumber: result.user.phoneNumber || null,
            };
            updateCustomer(customerCheck.customer, firebaseUser)
            console.log('OTP: Customer exists, navigating to home');
            if (!hasNavigated.current && !navigationAttempted.current) {
              console.log('OTP: Direct navigation to home');
              navigationAttempted.current = true;
              hasNavigated.current = true;
              setTimeout(() => {
                console.log('OTP: Navigating to home after delay');
                navigate(routes.home, { replace: true });
                console.log('Forced navigation to home');
              })

            }
          } else {
            // Customer doesn't exist - navigate to register
            console.log('OTP: Customer does not exist, navigating to register');
            console.log('OTP: hasNavigated.current:', hasNavigated.current);
            console.log('OTP: navigationAttempted.current:', navigationAttempted.current);
            if (!hasNavigated.current && !navigationAttempted.current) {
              console.log('OTP: Direct navigation to register');
              navigationAttempted.current = true;
              hasNavigated.current = true;
              navigate(routes.register, { replace: true });
            } else {
              console.log('OTP: Navigation already attempted, skipping');
            }
          }
        } catch (customerError) {
          console.error('OTP: Error checking customer:', customerError);
          // If we can't check customer, assume they need to register
          if (!hasNavigated.current && !navigationAttempted.current) {
            console.log('OTP: Customer check failed, navigating to register');
            navigationAttempted.current = true;
            hasNavigated.current = true;
            navigate(routes.register, { replace: true });
            console.log('Navigation function called');
          }
        }
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);

      let errorMessage = t("auth.errorMessages.otp.verificationFailed")

      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = t("auth.errorMessages.otp.verificationFailed");
      } else if (error.code === 'auth/code-expired') {
        errorMessage = t("auth.errorMessages.otp.codeExpired");
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100svh',
        height: '100svh',
        position: 'relative',
        background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
      }}
    >
      <Box sx={{ flex: isDesktop ? 0.3 : 0.5 }}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            color: 'white',
            pt: 4,
            pb: 4,
          }}
        >
          <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
        </Box>
      </Box>

      <Box sx={{ flex: 0.5 }}>
        <Box
          sx={{
            height: isDesktop ? 'auto' : '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            pt: isDesktop ? 4 : 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: isDesktop ? '24px' : '24px 24px 0 0',
              background: '#FFF',
              backdropFilter: 'blur(10px)',
              p: 4,
              width: '100%',
              height: '100%',
              maxWidth: isDesktop ? '400px' : '100%',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {/* Language Selector */}
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: 16,
                transform: 'translateY(-50%)',
                zIndex: 1,
                color: '#fff'
              }}
            >
              <LanguageSelector dropdownColor="#fff" />
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              {/* Phone Number Display */}
              <Typography
                sx={{
                  textAlign: 'center',
                  mb: 4,
                  color: '#3D3D3D',
                  fontSize: '16px',
                  fontWeight: 400,
                  fontFamily: 'Montserrat',
                  fontStyle: 'normal',
                  lineHeight: '22px',
                  letterSpacing: '0.28px',
                }}
              >
                {t("auth.otp.enterCodeSentTo")}
                <span style={{ fontWeight: 600, fontSize: '18px' }}>
                  {localPhoneNumber}
                </span>
              </Typography>

              {/* OTP Input Fields */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4 }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    type="tel"
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 600,
                      },
                    }}
                    sx={{
                      width: '50px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#F5F5F5',
                        border: '1px solid #D9E4CE',
                        height: '50px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '& .MuiInputBase-input': {
                          textAlign: 'center',
                          padding: '12px 8px',
                        },
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Resend Option */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#3d3d3d',
                    mb: 0.5,
                  }}
                >
                  {t("auth.otp.notReceived")}
                </Typography>
                <Link
                  type="button"
                  component="button"
                  variant="body2"
                  onClick={resendOTP}
                  sx={{
                    color: '#fc551d',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                  disabled={resendTimer > 0 || !isRecaptchaReady}
                >
                  {t("auth.otp.resend")} {resendTimer > 0 ? `(${resendTimer}s)` : ''}
                </Link>
              </Box>

              {/* Continue Button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || otp.join('').length !== 6}
                  sx={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    textTransform: 'none',
                    backgroundColor: '#FF5413',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#d2450eff',
                    },
                    '&:disabled': {
                      backgroundColor: '#E0E0E0',
                      color: '#999',
                    },
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                      {t("auth.messages.verifying")}
                    </>
                  ) : (
                    t("common.continue")
                  )}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>

            {/* reCAPTCHA container */}
            {/* This div is removed as reCAPTCHA is no longer used */}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
